import React, { useState } from "react";
import Modal from "react-modal";
import { Button } from "../../Button";
import { PartnerAccountViewModel } from "../../../api/generated/models/PartnerAccountViewModel";
import { PartnerAccountsService } from "../../../api/generated/services/PartnerAccountsService";
import { ReactComponent as CloseIcon } from "../../../assets/icons/x.svg";

interface RemovePartnerAccessModalProps {
  isOpen: boolean;
  partnerAccount: PartnerAccountViewModel | null;
  onClose: () => void;
  onSuccess: () => void;
}

const RemovePartnerAccessModal: React.FC<RemovePartnerAccessModalProps> = ({
  isOpen,
  partnerAccount,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  const handleConfirm = async () => {
    if (!partnerAccount?.id) {
      setError("Partner account ID is missing");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await PartnerAccountsService.postApiPartnerAccountsDeletePartnerAccount({
        partnerAccountId: partnerAccount.id,
      });
      handleClose();
      onSuccess();
    } catch (err: any) {
      console.error("Error removing partner access:", err);
      setError(
        err?.body?.message ||
          err?.message ||
          "Failed to remove access. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  if (!partnerAccount) {
    return null;
  }

  return (
    <Modal
      className="modal-content md:max-w-[500px]"
      overlayClassName="modal-overlay"
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Remove Access"
    >
      <div className="flex justify-between mb-6">
        <h2 className="text-primary font-medium text-xl">Remove Access</h2>
        <Button onClick={handleClose} variant="icon" disabled={loading}>
          <CloseIcon className="w-3 h-3" />
        </Button>
      </div>

      <div className="space-y-4">
        <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
          <p className="text-sm text-red-800">
            Are you sure you want to remove dashboard access for{" "}
            <span className="font-medium">{partnerAccount.email}</span>?
          </p>
          <p className="text-sm text-red-800 mt-2">
            This user will no longer be able to access the partner dashboard for
            this company.
          </p>
          <p className="text-sm text-red-800 mt-2 font-medium">
            This action cannot be undone.
          </p>
        </div>

        {error && (
          <div className="text-red-500 text-sm bg-red-50 border border-red-200 p-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="flex gap-3 justify-end pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            isLoading={loading}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            Remove Access
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default RemovePartnerAccessModal;
